import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't intercept 401s on the SSO callback page — it handles auth itself
      if (window.location.pathname === '/auth/sso') {
        return Promise.reject(error);
      }

      // Token expired or invalid
      localStorage.removeItem('token');

      localStorage.removeItem('auth_source');
      localStorage.removeItem('expiresAt');

      window.location.href = '/auth/session-expired';
    }
    return Promise.reject(error);
  }
);

export default api;

// Document API
export const documentsApi = {
  list: (blogId, params = {}) =>
    api.get(`/blogs/${blogId}/documents`, { params }),

  get: (id) =>
    api.get(`/documents/${id}`),

  getEditorMetadata: (id) =>
    api.get(`/documents/${id}/editor-metadata`),

  create: (blogId, data) =>
    api.post(`/blogs/${blogId}/documents`, data),

  update: (id, data) =>
    api.put(`/documents/${id}`, data),

  delete: (id) =>
    api.delete(`/documents/${id}`),

  duplicate: (id) =>
    api.post(`/documents/${id}/duplicate`),

  getRevisions: (id) =>
    api.get(`/documents/${id}/revisions`),

  getRevision: (revisionId) =>
    api.get(`/revisions/${revisionId}`),

  restoreRevision: (documentId, revisionId) =>
    api.post(`/documents/${documentId}/revisions/${revisionId}/restore`),

  deleteRevision: (revisionId) =>
    api.delete(`/revisions/${revisionId}`),

  sync: (id) =>
    api.post(`/documents/${id}/sync`),

  unpublish: (id) =>
    api.post(`/documents/${id}/unpublish`),

  getSyncStatus: (id) =>
    api.get(`/documents/${id}/sync-status`)
};

// Production Word document API. Signed editor configuration is returned by the
// backend and must be passed through without browser-side mutation.
export const wordDocumentsApi = {
  createBlank: (blogId, { name, description, starting_model }) =>
    api.post(`/blogs/${blogId}/word-documents`, {
      name,
      description,
      starting_model,
      creation_method: 'blank'
    }),

  upload: (blogId, data, onProgress) => {
    const { name, description, starting_model, file } = data || {};
    const formData = new FormData();
    formData.append('name', name);
    formData.append('description', description || '');
    formData.append('starting_model', starting_model);
    formData.append('creation_method', 'upload');
    formData.append('file', file);

    return api.post(`/blogs/${blogId}/word-documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress ? (event) => {
        if (!event.total) return;
        onProgress(Math.round((event.loaded * 100) / event.total));
      } : undefined
    });
  },

  get: (id) =>
    api.get(`/word-documents/${id}`),

  getEditorConfig: (id) =>
    api.get(`/word-documents/${id}/editor-config`),

  getFields: (id) =>
    api.get(`/word-documents/${id}/fields`),

  normalizeFields: (id, expectedRevision) =>
    api.post(`/word-documents/${id}/fields/normalize`, { expectedRevision }),

  publish: (id, expectedRevision, forceSaveAttemptId = null) =>
    api.post(`/word-documents/${id}/publish`, { expectedRevision, forceSaveAttemptId }),

  forceSave: (id, expectedEditorKey) =>
    api.post(`/word-documents/${id}/force-save`, { expectedEditorKey }),

  getForceSaveAttempt: (id, attemptId) =>
    api.get(`/word-documents/${id}/force-save-attempts/${encodeURIComponent(attemptId)}`),

  unpublish: (id, expectedPublication) =>
    api.delete(`/word-documents/${id}/publish`, { data: { expectedPublication } }),

  delete: (blogId, id) =>
    api.delete(`/blogs/${blogId}/word-documents/${id}`),

  download: (id) =>
    api.get(`/word-documents/${id}/download`, { responseType: 'blob' })
};

export const fontManagerApi = {
  list: () => api.get('/font-manager'),

  upload: (files, expectedFaceKey = null) => {
    const formData = new FormData();
    for (const file of files || []) formData.append('files', file);
    if (expectedFaceKey) formData.append('expectedFaceKey', expectedFaceKey);
    return api.post('/font-manager/fonts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  rollback: faceKey => api.post(`/font-manager/faces/${encodeURIComponent(faceKey)}/rollback`),
  deactivate: faceKey => api.delete(`/font-manager/faces/${encodeURIComponent(faceKey)}`),
  refresh: () => api.post('/font-manager/runtime/refresh')
};

export const managedFontsApi = {
  list: () => api.get('/fonts'),
  download: url => api.get(url.replace(/^\/api/, ''), { responseType: 'arraybuffer' })
};

// DEVELOPMENT SPIKE: authenticated ONLYOFFICE DOCX round-trip API
export const wordEditorSpikeApi = {
  create: (file) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post('/word-editor-spike/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  get: (id) =>
    api.get(`/word-editor-spike/documents/${id}`),

  getEditorConfig: (id) =>
    api.get(`/word-editor-spike/documents/${id}/editor-config`),

  getFields: (id) =>
    api.get(`/word-editor-spike/documents/${id}/fields`),

  normalizeFields: (id, expectedRevision) =>
    api.post(`/word-editor-spike/documents/${id}/fields/normalize`, { expectedRevision }),

  download: (id) =>
    api.get(`/word-editor-spike/documents/${id}/download`, {
      responseType: 'blob'
    })
};

// Users API (admin only)
export const usersApi = {
  list: () =>
    api.get('/users'),

  get: (id) =>
    api.get(`/users/${id}`),

  create: (data) =>
    api.post('/users', data),

  update: (id, data) =>
    api.put(`/users/${id}`, data),

  delete: (id) =>
    api.delete(`/users/${id}`),

  assignToBlog: (userId, blogId, role) =>
    api.post(`/users/${userId}/blogs`, { blogId, role }),

  removeFromBlog: (userId, blogId) =>
    api.delete(`/users/${userId}/blogs/${blogId}`)
};

// Blogs API
export const blogsApi = {
  list: () =>
    api.get('/blogs'),

  get: (id) =>
    api.get(`/blogs/${id}`),

  create: (data) =>
    api.post('/blogs', data),

  update: (id, data) =>
    api.put(`/blogs/${id}`, data),

  delete: (id) =>
    api.delete(`/blogs/${id}`),

  updateSettings: (id, settings) =>
    api.put(`/blogs/${id}/settings`, { settings })
};

// Upload API
export const uploadApi = {
  upload: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      }
    });
  },

  delete: (filename) =>
    api.delete(`/upload/${filename}`)
};

// Preferences API
export const preferencesApi = {
  get: () =>
    api.get('/preferences'),

  update: (data) =>
    api.put('/preferences', data)
};

// SSO API
export const ssoApi = {
  exchange: (token, signature) =>
    api.post('/auth/sso/exchange', { token, signature }),

  refresh: (refreshToken) =>
    api.post('/auth/sso/refresh', { refreshToken })
};

// WordPress Integration API
export const wordpressApi = {
  // Set token manually (directly paste JWT)
  setTokenManually: (blogId, token) =>
    api.post('/wordpress/token/manual', { blogId, token }),

  // Get token info (masked preview, not full token)
  getTokenInfo: (blogId) =>
    api.get(`/wordpress/token/${blogId}`),

  // Test WordPress connection
  testConnection: (blogId) =>
    api.get(`/wordpress/test/${blogId}`),

  // Get available models from WordPress
  getModels: (blogId) =>
    api.get(`/blogs/${blogId}/models`),

  // Get model details (attributes, relationships, accessors)
  getModelDetails: (blogId, modelClass) =>
    api.get(`/blogs/${blogId}/models/${encodeURIComponent(modelClass)}/details`),

  // Get the per-blog relationship option list for the pivot-builder popover
  getRelationshipOptions: (blogId) =>
    api.get(`/blogs/${blogId}/relationship-options`),

  // Search customers (contacts/accounts) via WordPress fulltext
  searchCustomers: (blogId, search, options = {}) =>
    api.get(`/blogs/${blogId}/customers/search`, { params: { search, ...options } }),

  // Get a single customer by type and ID
  getCustomer: (blogId, type, id) =>
    api.get(`/blogs/${blogId}/customers/${type}/${id}`)
};

// Customer Documents API
export const customerDocumentsApi = {
  list: (blogId, params = {}) =>
    api.get(`/customer-documents/${blogId}`, { params }),

  get: (id) =>
    api.get(`/customer-documents/doc/${id}`),

  create: (blogId, formData, onProgress) =>
    api.post(`/customer-documents/${blogId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded * 100) / e.total)) : undefined
    }),

  update: (id, data) =>
    api.put(`/customer-documents/doc/${id}`, data),

  delete: (id) =>
    api.delete(`/customer-documents/doc/${id}`),

  getEvents: (id) =>
    api.get(`/customer-documents/doc/${id}/events`),

  saveFields: (id, fields) =>
    api.put(`/customer-documents/doc/${id}/fields`, { fields }),

  send: (id) =>
    api.post(`/customer-documents/doc/${id}/send`),

  resend: (id) =>
    api.post(`/customer-documents/doc/${id}/resend`),

  void: (id) =>
    api.post(`/customer-documents/doc/${id}/void`),

  downloadUrl: (id) =>
    `/api/customer-documents/doc/${id}/download`
};

// Public Signing API (no auth required)
export const signingApi = {
  getDocument: (token) =>
    axios.get(`/api/signing/${token}`),

  getFile: (token) =>
    `/api/signing/${token}/file`,

  signField: (token, fieldId, value) =>
    axios.post(`/api/signing/${token}/sign`, { field_id: fieldId, value }),

  decline: (token, reason) =>
    axios.post(`/api/signing/${token}/decline`, { reason })
};
